<template>
  <section class="request-detail-conversation" data-testid="request-detail-conversation">
    <div class="request-detail-conversation__panel">
      <p class="request-detail-conversation__heading">Comments</p>

      <FcConversation
        :conversation-list="conversationList"
        :loading="false"
        :external-send="true"
        :can-comment="canComment"
        :users="mentionUsers"
        data-testid="request-detail-conversation__fc"
        @send="onSend"
      />
    </div>
  </section>
</template>

<script>
import './RequestDetailConversation.scss';
import FcConversation from '@/modules/financialclose/components/FcConversation/FcConversation.vue';

export default {
  name: 'RequestDetailConversation',
  components: { FcConversation },
  props: {
    request: {
      type: Object,
      required: true,
    },
    projectId: {
      type: String,
      required: true,
    },
    cycleId: {
      type: String,
      required: true,
    },
    locationName: {
      type: String,
      default: '',
    },
    requestSerial: {
      type: [String, Number],
      default: '',
    },
    canComment: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    /**
     * Lookup map for enriching IR comments (API returns user id only, not user_detail).
     * Same sources Comments uses (formDataV3) plus IM userList / info owners / session user.
     */
    userLookupMap() {
      const map = { ...(this.$store.getters['tasks/getUserMap'] || {}) };

      (this.$store.getters['informationManager/userList'] || []).forEach((user) => {
        if (user && user._id) map[user._id] = user;
      });

      (this.mentionUsers || []).forEach((user) => {
        if (user && user._id) map[user._id] = user;
      });

      const sessionUser = this.$store.state.user;
      const sessionId = sessionUser?.id || sessionUser?._id;
      if (sessionId) {
        const existing = map[sessionId] || {};
        map[sessionId] = {
          ...existing,
          _id: sessionId,
          name: sessionUser.name || existing.name || sessionUser.email || 'User',
          email: sessionUser.email || existing.email || '',
          profile_url: sessionUser.profile_url || existing.profile_url,
        };
      }

      return map;
    },
    conversationList() {
      const comments = Array.isArray(this.request?.comments) ? this.request.comments : [];
      return comments.filter((comment) => comment && typeof comment === 'object').map((comment) => {
        const userId = comment.user;
        return {
          ...comment,
          readby: Array.isArray(comment.readby) ? comment.readby : [],
          value: comment.value || comment.comment || '',
          user: userId,
          // Comments.vue expects user_detail.name / profile_url (task API embeds it; IR only sends user id)
          user_detail: this.resolveUserDetail(userId, comment.user_detail),
        };
      });
    },
    /**
     * Tag candidates: cycle users (getformdata) + info owners (infoowner/list).
     * Both are already in Vuex from Information Requests bootstrap — no new API calls.
     * Deduped by _id / email before passing into Comments via the `users` prop.
     */
    mentionUsers() {
      const cycleUsers = [
        ...(this.$store.getters['informationManager/userList'] || []),
        ...(this.$store.getters['tasks/getFormDataV3']?.users || []),
      ];
      const infoOwners = (this.$store.getters['informationManager/infoOwnerList'] || [])
        .filter((owner) => owner && owner.status !== false)
        .map((owner) => ({
          _id: owner._id,
          name: owner.name || owner.email || 'User',
          email: owner.email || '',
        }));

      const seenIds = new Set();
      const seenEmails = new Set();
      return [...cycleUsers, ...infoOwners].filter((user) => {
        if (!user) return false;
        const id = user._id;
        const email = (user.email || '').trim().toLowerCase();
        if (id && seenIds.has(id)) return false;
        if (email && seenEmails.has(email)) return false;
        if (id) seenIds.add(id);
        if (email) seenEmails.add(email);
        return Boolean(id);
      }).map((user) => ({
        _id: user._id,
        name: user.name || user.email || 'User',
        email: user.email || '',
        profile_url: user.profile_url,
      }));
    },
    currentUserId() {
      return this.$store.state.user?.id || this.$store.state.user?._id || '';
    },
  },
  watch: {
    conversationList: {
      handler() {
        this.markUnreadAsRead();
      },
      deep: true,
    },
  },
  mounted() {
    this.markUnreadAsRead();
  },
  methods: {
    /**
     * Build Comments.vue-compatible user_detail from comment.user id.
     * Prefers API user_detail when present; otherwise resolves from store maps / session.
     */
    resolveUserDetail(userId, existingDetail) {
      if (existingDetail && (existingDetail.name || existingDetail.email)) {
        return {
          _id: existingDetail._id || userId,
          name: existingDetail.name || existingDetail.email || 'User',
          email: existingDetail.email || '',
          profile_url: existingDetail.profile_url,
        };
      }

      const matched = userId ? this.userLookupMap[userId] : null;
      if (matched) {
        return {
          _id: matched._id || userId,
          name: matched.name || matched.email || 'User',
          email: matched.email || '',
          profile_url: matched.profile_url,
        };
      }

      return {
        _id: userId || '',
        name: 'User',
        email: '',
      };
    },
    async markUnreadAsRead() {
      if (!this.request?._id || !this.conversationList.length) return;
      const lastComment = this.conversationList[this.conversationList.length - 1];
      if (!lastComment || typeof lastComment !== 'object') return;
      const readby = Array.isArray(lastComment.readby) ? lastComment.readby : [];
      if (readby.indexOf(this.currentUserId) === -1) {
        await this.$store.dispatch('informationManager/markCommentAsRead', {
          requestId: this.request._id,
        });
      }
    },
    async onSend({ message, plainText, taggedUserIds, complete }) {
      // Prefer tagged message (includes @ name spans) so mentions render in the thread.
      const text = (message || plainText || '').trim();
      if (!text || !this.request?._id) {
        if (typeof complete === 'function') complete(false);
        return;
      }

      try {
        const result = await this.$store.dispatch('informationManager/addComment', {
          projectId: this.projectId,
          data: [{ comment: text, request: this.request._id }],
        });
        if (result?.status === 'success') {
          if (Array.isArray(taggedUserIds) && taggedUserIds.length) {
            const taggedUsers = taggedUserIds
              .map((id) => this.userLookupMap[id])
              .filter((user) => user && user.email);

            if (taggedUsers.length) {
              await this.$store.dispatch('informationManager/triggerTaggedNotification', {
                projectId: this.projectId,
                cycleId: this.cycleId,
                requestId: this.request._id,
                commentText: plainText || text,
                taggedUsers,
                requestMeta: {
                  serial: this.requestSerial || this.request.sno || '',
                  description: this.request.description || '',
                  infoOwner: (this.request.info_owner_detail || {}).name
                    || this.request.info_owner_name
                    || '',
                  location: this.locationName || '',
                  dueDate: this.request.duedate
                    ? this.$nova.formatDate(this.request.duedate)
                    : '',
                },
              });
            }
          }

          await this.$store.dispatch('informationManager/fetchRequests');
          this.$emit('comment-added');
          if (typeof complete === 'function') complete(true);
          return;
        }
        if (typeof complete === 'function') complete(false);
      } catch (err) {
        if (typeof complete === 'function') complete(false);
      }
    },
  },
};
</script>
